/**
 * Shared Quick Entry page for the 4 preparatory stages.
 * Sprint 0146 QE.
 *
 * Same shape as the Carding Quick Entry — mirror the machine display,
 * type what you see, server derives everything else, faults > 0 open
 * a Why? drawer for root cause + corrective action.
 *
 * Stage-specific bits handled via the `kind` prop:
 *   • Lap Former: optional count-based input (laps × avg weight)
 *   • Comber: input lap kg / noil / waste for reconciliation
 *   • DF Breaker / Finisher: standard production + faults
 *
 * Used by 4 thin route components — BreakerDFQuickEntryPage etc.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Alert, Button, Card, Col, DatePicker, Divider, Drawer, Input,
  InputNumber, Row, Select, Space, Tag, Typography, message,
} from 'antd'
import {
  CheckCircleOutlined, SaveOutlined, WarningOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'

import { KIND_META, preparatoryApi, type PreparatoryKind } from '@/features/production/preparatoryApi'
import { usePlantId } from '@/shared/hooks/usePlantId'
import apiClient from '@/shared/api/client'

const { Title, Text, Paragraph } = Typography

const FAULT_TYPES = [
  'DOUBLE_LAP', 'OTHER_FAULTS', 'SLIVER_BREAK', 'CHUTE_FAULTS', 'DOFFS',
] as const
type FaultType = typeof FAULT_TYPES[number]

interface MachineLite { id: string; machine_code: string; machine_name: string }
interface ShiftLite { id: string; shift_code: string; shift_name: string }
interface CountLite { id: string; count_code: string; count_ne: number }

// Per-stage root-cause chip lists. DF / Lap / Comber have different
// failure modes than Carding — keep them domain-relevant.
const ROOT_CAUSE_CHIPS: Record<PreparatoryKind, Record<FaultType, string[]>> = {
  'breaker-df': {
    DOUBLE_LAP: ['Can underweight', 'Creel slip', 'Sliver too thin'],
    SLIVER_BREAK: ['Trumpet blocked', 'Draft too high', 'Tension low'],
    CHUTE_FAULTS: ['n/a'],
    OTHER_FAULTS: ['Power dip', 'Mechanical', 'Electrical'],
    DOFFS: [],
  },
  'finisher-df': {
    DOUBLE_LAP: ['Creel slip', 'Sliver too thin'],
    SLIVER_BREAK: ['Trumpet blocked', 'Draft too high', 'Stretch break'],
    CHUTE_FAULTS: ['n/a'],
    OTHER_FAULTS: ['Power dip', 'Mechanical', 'Electrical'],
    DOFFS: [],
  },
  'lap-former': {
    DOUBLE_LAP: ['Lap wrap break', 'Roller jam'],
    SLIVER_BREAK: ['Sliver creel fault', 'Tension too low'],
    CHUTE_FAULTS: ['n/a'],
    OTHER_FAULTS: ['Power dip', 'Mechanical', 'Electrical'],
    DOFFS: [],
  },
  comber: {
    DOUBLE_LAP: ['Lap feed jam', 'Top comb broken'],
    SLIVER_BREAK: ['Trumpet blocked', 'Detaching roll slip'],
    CHUTE_FAULTS: ['n/a'],
    OTHER_FAULTS: ['Power dip', 'Mechanical', 'Electrical', 'Nipper trouble'],
    DOFFS: [],
  },
}

const CORRECTIVE_CHIPS: Record<FaultType, string[]> = {
  DOUBLE_LAP: ['Cleared jam', 'Reset creel', 'Replaced sliver'],
  SLIVER_BREAK: ['Cleaned trumpet', 'Adjusted draft', 'Replaced guide'],
  CHUTE_FAULTS: [],
  OTHER_FAULTS: ['Restarted', 'Called maintenance', 'Logged for next shift'],
  DOFFS: [],
}

export default function PreparatoryQuickEntryPage({
  kind,
}: { kind: PreparatoryKind }) {
  const meta = KIND_META[kind]
  const { plantId } = usePlantId()

  const [machineId, setMachineId] = useState<string | undefined>()
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [countId, setCountId] = useState<string | undefined>()
  const [logDate, setLogDate] = useState<Dayjs>(dayjs())

  // Production fields (shared)
  const [kgs, setKgs] = useState<number | null>(null)
  const [runH, setRunH] = useState<number | null>(null)
  const [runM, setRunM] = useState<number | null>(null)
  const [idleH, setIdleH] = useState<number | null>(null)
  const [idleM, setIdleM] = useState<number | null>(null)
  const [pfH, setPfH] = useState<number | null>(null)
  const [pfM, setPfM] = useState<number | null>(null)
  const [mcEff, setMcEff] = useState<number | null>(null)

  // Lap Former — count-based optional inputs
  const [lapsProduced, setLapsProduced] = useState<number | null>(null)
  const [avgLapWeight, setAvgLapWeight] = useState<number | null>(null)

  // Comber — reconciliation inputs
  const [inputLapKg, setInputLapKg] = useState<number | null>(null)
  const [noilKg, setNoilKg] = useState<number | null>(null)
  const [wasteKg, setWasteKg] = useState<number | null>(null)

  // Fault counters
  const [doubleLap, setDoubleLap] = useState<number>(0)
  const [otherFaults, setOtherFaults] = useState<number>(0)
  const [sliverBreak, setSliverBreak] = useState<number>(0)
  const [chuteFaults, setChuteFaults] = useState<number>(0)
  const [doffs, setDoffs] = useState<number>(0)

  type FaultMeta = { root_cause: string | null; corrective_action: string | null }
  const [faultMeta, setFaultMeta] = useState<Record<FaultType, FaultMeta>>({
    DOUBLE_LAP: { root_cause: null, corrective_action: null },
    OTHER_FAULTS: { root_cause: null, corrective_action: null },
    SLIVER_BREAK: { root_cause: null, corrective_action: null },
    CHUTE_FAULTS: { root_cause: null, corrective_action: null },
    DOFFS: { root_cause: null, corrective_action: null },
  })
  const [whyOpen, setWhyOpen] = useState<FaultType | null>(null)
  const [remarks, setRemarks] = useState('')

  // ── Master-data loads ──────────────────────────────────────────────
  const machinesQ = useQuery({
    enabled: !!plantId,
    queryKey: ['quick-machines', kind, plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/machines', {
        params: { plant_id: plantId, is_active: true, size: 500 },
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw
        .filter((m) => (m.machine_type ?? m.section ?? '').toUpperCase() === meta.machineType.toUpperCase())
        .map((m): MachineLite => ({
          id: m.id,
          machine_code: m.machine_code ?? m.machine_id_str ?? '',
          machine_name: m.machine_name ?? m.name ?? '',
        }))
        .sort((a, b) => a.machine_code.localeCompare(b.machine_code, undefined, { numeric: true }))
    },
  })

  const shiftsQ = useQuery({
    enabled: !!plantId,
    queryKey: ['quick-shifts-prep', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/shifts', {
        params: { plant_id: plantId, is_active: true },
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw.map((s): ShiftLite => ({
        id: s.id, shift_code: s.shift_code, shift_name: s.shift_name,
      }))
    },
  })

  const countsQ = useQuery({
    queryKey: ['quick-counts-prep'],
    queryFn: async () => {
      const r = await apiClient.get('/masters/counts', { params: { size: 500 } })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw.map((c): CountLite => ({
        id: c.id, count_code: c.count_code ?? '', count_ne: c.count_ne ?? 0,
      }))
    },
  })

  // ── Submit ─────────────────────────────────────────────────────────
  const saveMut = useMutation({
    mutationFn: (body: Parameters<typeof preparatoryApi.quickEntry>[1]) =>
      preparatoryApi.quickEntry(kind, body),
    onSuccess: (res) => {
      const recon = res.calc?.comber_reconciliation
      let extra = ''
      if (recon && recon.within_tolerance === false) {
        extra = ` · COMBER RECONCILIATION BREACH (${recon.unaccounted_pct?.toFixed(2)}%)`
      }
      message.success(
        `Saved ${res.log_no} · ${res.alerts_raised} alert${res.alerts_raised === 1 ? '' : 's'}${extra}`
      )
      const list = machinesQ.data ?? []
      const idx = list.findIndex((m) => m.id === machineId)
      if (idx >= 0 && idx < list.length - 1) {
        setMachineId(list[idx + 1].id)
      }
      // Reset numeric fields
      setKgs(null); setRunH(null); setRunM(null)
      setIdleH(null); setIdleM(null); setPfH(null); setPfM(null)
      setMcEff(null)
      setLapsProduced(null); setAvgLapWeight(null)
      setInputLapKg(null); setNoilKg(null); setWasteKg(null)
      setDoubleLap(0); setOtherFaults(0); setSliverBreak(0)
      setChuteFaults(0); setDoffs(0)
      setFaultMeta({
        DOUBLE_LAP: { root_cause: null, corrective_action: null },
        OTHER_FAULTS: { root_cause: null, corrective_action: null },
        SLIVER_BREAK: { root_cause: null, corrective_action: null },
        CHUTE_FAULTS: { root_cause: null, corrective_action: null },
        DOFFS: { root_cause: null, corrective_action: null },
      })
      setRemarks('')
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const missingWhy: FaultType[] = useMemo(() => {
    const out: FaultType[] = []
    const pairs: Array<[FaultType, number]> = [
      ['DOUBLE_LAP', doubleLap],
      ['OTHER_FAULTS', otherFaults],
      ['SLIVER_BREAK', sliverBreak],
      ['CHUTE_FAULTS', chuteFaults],
    ]
    for (const [t, c] of pairs) {
      if (c > 0 && !faultMeta[t].root_cause?.trim()) out.push(t)
    }
    return out
  }, [doubleLap, otherFaults, sliverBreak, chuteFaults, faultMeta])

  const handleSave = () => {
    if (!plantId || !machineId || !shiftId) {
      message.warning('Pick machine and shift first')
      return
    }
    if (missingWhy.length > 0) {
      message.warning(
        `Fill the root cause for: ${missingWhy.join(', ')}. Tap the red faults to open Why?`
      )
      return
    }
    const runMin = (runH ?? 0) * 60 + (runM ?? 0)
    const idleMin = (idleH ?? 0) * 60 + (idleM ?? 0)
    const pfMin = (pfH ?? 0) * 60 + (pfM ?? 0)
    const faults: Array<{
      fault_type: FaultType; count: number
      root_cause?: string | null; corrective_action?: string | null
    }> = []
    const pairs: Array<[FaultType, number]> = [
      ['DOUBLE_LAP', doubleLap],
      ['OTHER_FAULTS', otherFaults],
      ['SLIVER_BREAK', sliverBreak],
      ['CHUTE_FAULTS', chuteFaults],
      ['DOFFS', doffs],
    ]
    for (const [t, c] of pairs) {
      if (c > 0) {
        faults.push({
          fault_type: t, count: c,
          root_cause: faultMeta[t].root_cause,
          corrective_action: faultMeta[t].corrective_action,
        })
      }
    }
    saveMut.mutate({
      plant_id: plantId,
      machine_id: machineId,
      shift_id: shiftId,
      log_date: logDate.format('YYYY-MM-DD'),
      count_id: countId ?? null,
      production_kg: kgs,
      run_time_minutes: runMin || null,
      idle_time_minutes: idleMin || null,
      power_fail_minutes: pfMin || null,
      machine_efficiency_pct: mcEff,
      // Stage-specific extras (server ignores unset ones)
      laps_produced: kind === 'lap-former' ? lapsProduced : null,
      avg_lap_weight_kg: kind === 'lap-former' ? avgLapWeight : null,
      input_lap_kg: kind === 'comber' ? inputLapKg : null,
      noil_kg: kind === 'comber' ? noilKg : null,
      waste_kg: kind === 'comber' ? wasteKg : null,
      faults,
      remarks: remarks || null,
    })
  }

  const numField = (
    value: number | null,
    setter: (v: number | null) => void,
    placeholder = '',
    step = 1,
  ) => (
    <InputNumber
      value={value}
      onChange={(v) => setter(v as number | null)}
      placeholder={placeholder}
      style={{ width: '100%', height: 44, fontSize: 16 }}
      step={step}
      inputMode="decimal"
    />
  )

  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: '0 auto' }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        {meta.shortLabel} Quick Entry
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Mirror the {meta.shortLabel} machine display. Save snaps to the
        next machine in the section.
      </Paragraph>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Text strong>Machine</Text>
            <Select
              showSearch
              style={{ width: '100%', marginTop: 4 }}
              value={machineId}
              onChange={setMachineId}
              placeholder={`Pick ${meta.machineType}…`}
              options={(machinesQ.data ?? []).map((m) => ({
                label: `${m.machine_code} — ${m.machine_name}`, value: m.id,
              }))}
              filterOption={(input, opt) =>
                (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={12} sm={6}>
            <Text strong>Shift</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={shiftId} onChange={setShiftId}
              placeholder="A / B / C"
              options={(shiftsQ.data ?? []).map((s) => ({
                label: s.shift_code, value: s.id,
              }))}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Text strong>Date</Text>
            <DatePicker
              value={logDate}
              onChange={(d) => d && setLogDate(d)}
              style={{ width: '100%', marginTop: 4 }}
              format="DD-MMM-YYYY"
              allowClear={false}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Text strong>Count</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              value={countId} onChange={setCountId}
              placeholder="(any)"
              options={(countsQ.data ?? []).map((c) => ({
                label: `${c.count_code} (${c.count_ne}s)`, value: c.id,
              }))}
            />
          </Col>
        </Row>
      </Card>

      <Row gutter={12}>
        <Col xs={24} lg={16}>
          <Card size="small" title="Production" style={{ marginBottom: 12 }}>
            <Row gutter={12}>
              <Col xs={12} sm={6}>
                <Text>Kgs</Text>
                {numField(kgs, setKgs, '0.00', 0.01)}
              </Col>
              <Col xs={12} sm={6}>
                <Text>M/c Eff (%)</Text>
                {numField(mcEff, setMcEff, '85.00', 0.01)}
              </Col>
            </Row>
            <Divider style={{ margin: '12px 0' }} />
            <Row gutter={8}>
              <Col span={8}>
                <Text>Run Time</Text>
                <Space.Compact style={{ width: '100%' }}>
                  {numField(runH, setRunH, 'hh')}
                  {numField(runM, setRunM, 'mm')}
                </Space.Compact>
              </Col>
              <Col span={8}>
                <Text>Idle Time</Text>
                <Space.Compact style={{ width: '100%' }}>
                  {numField(idleH, setIdleH, 'hh')}
                  {numField(idleM, setIdleM, 'mm')}
                </Space.Compact>
              </Col>
              <Col span={8}>
                <Text>Power Fail</Text>
                <Space.Compact style={{ width: '100%' }}>
                  {numField(pfH, setPfH, 'hh')}
                  {numField(pfM, setPfM, 'mm')}
                </Space.Compact>
              </Col>
            </Row>
          </Card>

          {kind === 'lap-former' && (
            <Card
              size="small"
              title="Count-based production (preferred for Lap Former)"
              style={{ marginBottom: 12 }}
            >
              <Alert
                type="info"
                style={{ marginBottom: 8, fontSize: 12 }}
                message="Operators usually count laps + weigh one. Server picks count-based over speed-based when both are filled."
              />
              <Row gutter={12}>
                <Col xs={12}>
                  <Text>Laps produced</Text>
                  {numField(lapsProduced, setLapsProduced, '0')}
                </Col>
                <Col xs={12}>
                  <Text>Avg lap weight (kg)</Text>
                  {numField(avgLapWeight, setAvgLapWeight, '22.5', 0.1)}
                </Col>
              </Row>
            </Card>
          )}

          {kind === 'comber' && (
            <Card
              size="small"
              title="Comber reconciliation (input → output + noil + waste)"
              style={{ marginBottom: 12 }}
            >
              <Alert
                type="info"
                style={{ marginBottom: 8, fontSize: 12 }}
                message="Fill these to fire the reconciliation alert. >2% unaccounted → GM notified."
              />
              <Row gutter={12}>
                <Col xs={8}>
                  <Text>Input lap kg</Text>
                  {numField(inputLapKg, setInputLapKg, '0.00', 0.01)}
                </Col>
                <Col xs={8}>
                  <Text>Noil kg</Text>
                  {numField(noilKg, setNoilKg, '0.00', 0.01)}
                </Col>
                <Col xs={8}>
                  <Text>Waste kg</Text>
                  {numField(wasteKg, setWasteKg, '0.00', 0.01)}
                </Col>
              </Row>
            </Card>
          )}

          <Card size="small" title="Fault counters" style={{ marginBottom: 12 }}>
            <FaultRow label="Double Lap" count={doubleLap} onChange={setDoubleLap}
              hasWhy={!!faultMeta.DOUBLE_LAP.root_cause}
              onWhy={() => setWhyOpen('DOUBLE_LAP')} />
            <FaultRow label="Other Faults" count={otherFaults} onChange={setOtherFaults}
              hasWhy={!!faultMeta.OTHER_FAULTS.root_cause}
              onWhy={() => setWhyOpen('OTHER_FAULTS')} />
            <FaultRow label="Sliver Break" count={sliverBreak} onChange={setSliverBreak}
              hasWhy={!!faultMeta.SLIVER_BREAK.root_cause}
              onWhy={() => setWhyOpen('SLIVER_BREAK')} />
            <FaultRow label="Chute Faults" count={chuteFaults} onChange={setChuteFaults}
              hasWhy={!!faultMeta.CHUTE_FAULTS.root_cause}
              onWhy={() => setWhyOpen('CHUTE_FAULTS')} />
            <FaultRow label="No of Doffs" count={doffs} onChange={setDoffs}
              hasWhy={true} onWhy={() => {}} noWhyButton />
          </Card>

          <Card size="small" title="Remarks (optional)" style={{ marginBottom: 12 }}>
            <Input.TextArea rows={2} value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Any notes for next shift / supervisor…" />
          </Card>

          <Button
            type="primary" block size="large" icon={<SaveOutlined />}
            loading={saveMut.isPending}
            disabled={!machineId || !shiftId || missingWhy.length > 0}
            onClick={handleSave}
            style={{ height: 56, fontSize: 18 }}
          >
            {missingWhy.length > 0
              ? `Fill "Why?" for ${missingWhy.length} fault${missingWhy.length === 1 ? '' : 's'} first`
              : 'Save & next machine'}
          </Button>
        </Col>

        <Col xs={24} lg={8}>
          <Card size="small" title={`What's auto-derived (${meta.shortLabel})`}>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              Computed on Save from your inputs + the active standard.
              Comes back in the response and writes to the production log.
            </Paragraph>
            <ul style={{ fontSize: 13, marginBottom: 0 }}>
              <li>Theoretical kg (perfect run)</li>
              <li>Expected kg (theoretical × std eff)</li>
              <li>Actual efficiency vs theoretical</li>
              {kind === 'comber' && (
                <>
                  <li>Noil %</li>
                  <li>Realization %</li>
                  <li>Comber reconciliation (within / breach)</li>
                </>
              )}
            </ul>
          </Card>
        </Col>
      </Row>

      <Drawer
        title={
          <Space>
            <WarningOutlined style={{ color: '#fa8c16' }} />
            <span>Why? — {whyOpen?.replace('_', ' ')}</span>
          </Space>
        }
        open={!!whyOpen}
        onClose={() => setWhyOpen(null)}
        width={420}
      >
        {whyOpen && (
          <WhyForm
            kind={kind}
            faultType={whyOpen}
            meta={faultMeta[whyOpen]}
            onSave={(meta) => {
              setFaultMeta({ ...faultMeta, [whyOpen]: meta })
              setWhyOpen(null)
            }}
          />
        )}
      </Drawer>
    </div>
  )
}

function FaultRow({
  label, count, onChange, hasWhy, onWhy, noWhyButton = false,
}: {
  label: string; count: number; onChange: (n: number) => void
  hasWhy: boolean; onWhy: () => void; noWhyButton?: boolean
}) {
  const needsWhy = count > 0 && !hasWhy && !noWhyButton
  return (
    <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
      <Col span={10}><Text>{label}</Text></Col>
      <Col span={8}>
        <InputNumber
          min={0} value={count}
          onChange={(v) => onChange(Number(v ?? 0))}
          style={{ width: '100%', height: 40, fontSize: 16 }}
          inputMode="numeric"
        />
      </Col>
      <Col span={6}>
        {!noWhyButton && count > 0 && (
          <Button
            danger={needsWhy} size="small"
            type={needsWhy ? 'primary' : 'default'}
            onClick={onWhy} block
          >
            {hasWhy ? '✓ Why?' : 'Why?'}
          </Button>
        )}
      </Col>
    </Row>
  )
}

function WhyForm({
  kind, faultType, meta, onSave,
}: {
  kind: PreparatoryKind
  faultType: FaultType
  meta: { root_cause: string | null; corrective_action: string | null }
  onSave: (m: { root_cause: string | null; corrective_action: string | null }) => void
}) {
  const [cause, setCause] = useState(meta.root_cause ?? '')
  const [action, setAction] = useState(meta.corrective_action ?? '')
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <div>
        <Text strong>Root cause</Text>
        <Space wrap size={[6, 6]} style={{ marginTop: 6, marginBottom: 6 }}>
          {ROOT_CAUSE_CHIPS[kind][faultType].map((c) => (
            <Tag.CheckableTag
              key={c} checked={cause === c}
              onChange={() => setCause(c)}
            >
              {c}
            </Tag.CheckableTag>
          ))}
        </Space>
        <Input.TextArea
          rows={2} value={cause}
          onChange={(e) => setCause(e.target.value)}
          placeholder="Tap a chip or type your own"
        />
      </div>
      <div>
        <Text strong>Corrective action taken</Text>
        <Space wrap size={[6, 6]} style={{ marginTop: 6, marginBottom: 6 }}>
          {CORRECTIVE_CHIPS[faultType].map((c) => (
            <Tag.CheckableTag
              key={c} checked={action === c}
              onChange={() => setAction(c)}
            >
              {c}
            </Tag.CheckableTag>
          ))}
        </Space>
        <Input.TextArea
          rows={2} value={action}
          onChange={(e) => setAction(e.target.value)}
          placeholder="What did you do about it?"
        />
      </div>
      <Button
        type="primary" block disabled={!cause.trim()}
        onClick={() => onSave({
          root_cause: cause.trim() || null,
          corrective_action: action.trim() || null,
        })}
      >
        Save reason
      </Button>
    </Space>
  )
}
